import React, { useEffect, useState } from 'react';
import { RefreshCw, AlertCircle, Receipt, FileText } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAllPayments, createManualPayment } from '../api/payments.api';
import { formatDateTime24 } from '../../utils/formatTime24';
import { getCustomerWithCamaras } from '../api/AdminDashboard.api';

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '---';
  const numericAmount = Number(amount);
  const formatted = Number.isNaN(numericAmount) ? amount : numericAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${String(currency).toUpperCase()} ${formatted}` : formatted;
};

const formatDate = (value) => {
  if (!value) return '---';
  return formatDateTime24(value, { fallback: '---' });
};

const STATUS_STYLES = {
  paid: 'bg-green-100 text-green-700',
  succeeded: 'bg-green-100 text-green-700',
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-slate-400">---</span>;
  const style = STATUS_STYLES[String(status).toLowerCase()] || 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${style}`}>
      {status}
    </span>
  );
};

const AllPaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchPayments = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getAllPayments();
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(true);
      toast.error('Error al obtener los pagos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={fetchPayments}
          disabled={loading}
          className="flex items-center gap-2 min-h-[44px] lg:min-h-0 px-4 py-2 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo pagos...</p>
        </div>
      )}

      {!loading && error && payments.length === 0 && (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">No se pudieron obtener los pagos</p>
        </div>
      )}

      {!loading && !error && payments.length === 0 && (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Receipt className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">No se encontraron pagos</p>
        </div>
      )}

      {!loading && payments.length > 0 && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Todos los pagos</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">Cliente</th>
                  <th className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">Monto</th>
                  <th className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">Estado</th>
                  <th className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">MRR</th>
                  <th className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">Origen</th>
                  <th className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => {
                  const data = payment.data || {};
                  return (
                    <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{data.customerId || '---'}</td>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{formatAmount(data.amount, data.currency)}</td>
                      <td className="py-3 px-4 whitespace-nowrap"><StatusBadge status={data.paymentStatus} /></td>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{data.mrrAmount ?? '---'}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {data.manualCreated ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">Manual</span>
                        ) : (
                          <span className="text-slate-400">---</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{formatDate(data.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">Total de {payments.length} pagos</p>
        </div>
      )}
    </div>
  );
};

const INITIAL_FORM_STATE = {
  customerId: '',
  customerName: '',
  email: '',
  amount: '',
  currency: '',
  paymentStatus: '',
  mrrAmount: '',
  licenceType: '',
  licenceExtension: '',
  cameraQuantity: '',
  method: '',
  daysLeft: '',
  hasIVA: false,
  isRenewal: false,
  notes: '',
};

const NUMERIC_FIELDS = ['amount', 'mrrAmount', 'cameraQuantity', 'daysLeft'];

const buildManualPaymentPayload = (formData) => {
  const payload = {};
  Object.entries(formData).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      payload[key] = value;
      return;
    }
    if (value === '' || value === null || value === undefined) return;
    payload[key] = NUMERIC_FIELDS.includes(key) ? Number(value) : value;
  });
  return payload;
};

const CustomerAutocomplete = ({ value, customers, loading, onSelect }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  const filtered = (() => {
    const q = query.trim().toLowerCase();
    const pool = q
      ? customers.filter((c) => c.customerId?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q))
      : customers;
    return pool.slice(0, 20);
  })();

  const handlePick = (customer) => {
    onSelect(customer);
    setQuery(customer.name ? `${customer.customerId} — ${customer.name}` : customer.customerId);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onSelect(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={loading ? 'Cargando clientes...' : 'Buscar por ID o nombre...'}
        disabled={loading}
        className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      />
      {open && !loading && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg">
          {filtered.length > 0 ? (
            filtered.map((c) => (
              <button
                type="button"
                key={c.customerId}
                onMouseDown={() => handlePick(c)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
              >
                <span className="font-semibold text-slate-700">{c.customerId}</span>
                {c.name && <span className="text-slate-400"> — {c.name}</span>}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-slate-400">Sin coincidencias</div>
          )}
        </div>
      )}
    </div>
  );
};

const ManualPaymentTab = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  useEffect(() => {
    const loadCustomers = async () => {
      setCustomersLoading(true);
      try {
        const data = await getCustomerWithCamaras();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error('No se pudo cargar la lista de clientes');
      } finally {
        setCustomersLoading(false);
      }
    };
    loadCustomers();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSelectCustomer = (customer) => {
    setFormData((prev) => ({
      ...prev,
      customerId: customer ? customer.customerId : '',
      customerName: customer ? (customer.name || '') : '',
      email: customer ? (customer.mainUserEmail || '') : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId.trim()) {
      toast.error('Elegí un cliente existente de la lista');
      return;
    }
    setSubmitting(true);
    try {
      await createManualPayment(buildManualPaymentPayload(formData));
      toast.success('Pago manual cargado correctamente');
      setFormData(INITIAL_FORM_STATE);
    } catch (err) {
      toast.error('Error al cargar el pago manual');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FileText className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-800">Cargar pago manual</h3>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label htmlFor="customerId" className="block text-slate-700 font-semibold text-sm mb-1 after:content-['*'] after:ml-1 after:text-red-500">
            Cliente
          </label>
          <CustomerAutocomplete
            value={formData.customerId}
            customers={customers}
            loading={customersLoading}
            onSelect={handleSelectCustomer}
          />
        </div>

        <div>
          <label htmlFor="customerName" className="block text-slate-700 font-semibold text-sm mb-1">Nombre del cliente</label>
          <input type="text" id="customerName" name="customerName" value={formData.customerName} onChange={handleChange}
            readOnly={!!formData.customerId}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 read-only:bg-slate-50 read-only:text-slate-500" />
        </div>

        <div>
          <label htmlFor="email" className="block text-slate-700 font-semibold text-sm mb-1">Email</label>
          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
            readOnly={!!formData.customerId}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 read-only:bg-slate-50 read-only:text-slate-500" />
        </div>

        <div>
          <label htmlFor="amount" className="block text-slate-700 font-semibold text-sm mb-1">Monto</label>
          <input type="number" step="0.01" id="amount" name="amount" value={formData.amount} onChange={handleChange}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="currency" className="block text-slate-700 font-semibold text-sm mb-1">Moneda</label>
          <input type="text" id="currency" name="currency" value={formData.currency} onChange={handleChange}
            placeholder="USD, ARS..."
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="paymentStatus" className="block text-slate-700 font-semibold text-sm mb-1">Estado del pago</label>
          <input type="text" id="paymentStatus" name="paymentStatus" value={formData.paymentStatus} onChange={handleChange}
            placeholder="paid, pending..."
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="mrrAmount" className="block text-slate-700 font-semibold text-sm mb-1">MRR</label>
          <input type="number" step="0.01" id="mrrAmount" name="mrrAmount" value={formData.mrrAmount} onChange={handleChange}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <p className="text-xs text-slate-400 mt-1">Completar para que el cliente quede clasificado como "mensual"</p>
        </div>

        <div>
          <label htmlFor="licenceType" className="block text-slate-700 font-semibold text-sm mb-1">Tipo de licencia</label>
          <input type="text" id="licenceType" name="licenceType" value={formData.licenceType} onChange={handleChange}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="licenceExtension" className="block text-slate-700 font-semibold text-sm mb-1">Extensión de licencia</label>
          <input type="text" id="licenceExtension" name="licenceExtension" value={formData.licenceExtension} onChange={handleChange}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="cameraQuantity" className="block text-slate-700 font-semibold text-sm mb-1">Cantidad de cámaras</label>
          <input type="number" id="cameraQuantity" name="cameraQuantity" min="0" step="1" value={formData.cameraQuantity} onChange={handleChange}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="method" className="block text-slate-700 font-semibold text-sm mb-1">Método</label>
          <input type="text" id="method" name="method" value={formData.method} onChange={handleChange}
            placeholder="Transferencia, efectivo..."
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div>
          <label htmlFor="daysLeft" className="block text-slate-700 font-semibold text-sm mb-1">Días restantes</label>
          <input type="number" id="daysLeft" name="daysLeft" min="0" step="1" value={formData.daysLeft} onChange={handleChange}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex items-center gap-2 pt-6">
          <input type="checkbox" id="hasIVA" name="hasIVA" checked={formData.hasIVA} onChange={handleChange}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
          <label htmlFor="hasIVA" className="text-slate-700 font-semibold text-sm">Tiene IVA</label>
        </div>

        <div className="flex items-center gap-2 pt-6">
          <input type="checkbox" id="isRenewal" name="isRenewal" checked={formData.isRenewal} onChange={handleChange}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
          <label htmlFor="isRenewal" className="text-slate-700 font-semibold text-sm">Es renovación</label>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="notes" className="block text-slate-700 font-semibold text-sm mb-1">Notas</label>
          <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={3}
            className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="sm:col-span-2 flex justify-end pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-3 rounded-2xl font-bold text-sm bg-slate-900 text-white shadow-lg hover:bg-slate-800 disabled:opacity-50 transition-all"
          >
            {submitting ? 'Cargando...' : 'Cargar pago'}
          </button>
        </div>
      </form>
    </div>
  );
};

const TABS = [
  { id: 'allPayments', label: 'Todos los pagos', component: AllPaymentsTab },
  { id: 'manualPayment', label: 'Cargar pago manual', component: ManualPaymentTab },
];

const LicenciasDashboard = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const ActiveComponent = TABS.find((tab) => tab.id === activeTab)?.component;

  return (
    <div className="p-8 bg-slate-50 min-h-screen font-sans">
      <div className="mb-10">
        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Licencias</h1>
        <p className="text-slate-500 font-medium mt-1">Pagos y licencias de clientes</p>
      </div>

      <div className="flex bg-white p-2 rounded-3xl border border-slate-100 w-fit gap-2 mb-8 shadow-sm overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all ${
              activeTab === tab.id ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {ActiveComponent && <ActiveComponent />}

      <ToastContainer />
    </div>
  );
};

export default LicenciasDashboard;
